import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';

/**
 * Custom hook that encapsulates all business logic for ConnectTab component
 * @param {Object} props - Component props
 * @param {Array} props.tabData - Array of tab configuration objects
 * @param {string} props.variant - Tab variant style ('primary', 'secondary', 'rebranding')
 * @param {Object} props.digitalData - Digital data context for analytics
 * @returns {Object} - Processed data and handlers
 */
export const useConnectTab = ({ tabData = [], variant = 'primary', digitalData }) => {
  const router = useRouter();

  const getActiveTabIndex = useCallback(() => {
    const planQuery = router?.query?.plan;
    return planQuery ? Number(planQuery) : 0;
  }, [router?.query?.plan]);

  const [activeTab, setActiveTab] = useState(() => getActiveTabIndex());

  const handleSelectedTab = useCallback((tabIndex) => {
    setActiveTab(tabIndex);
  }, []);

  const isLeftTabActive = useCallback(() => activeTab === 0, [activeTab]);
  const isRightTabActive = useCallback(() => activeTab === 1, [activeTab]);

  const processedTabData = useMemo(() => {
    if (!Array.isArray(tabData) || tabData.length < 2) {
      return {
        leftTab: null,
        rightTab: null,
        hasValidTabs: false,
      };
    }

    const [leftTabConfig, rightTabConfig] = tabData;

    return {
      leftTab: {
        title: leftTabConfig?.title?.(isLeftTabActive()) ?? '',
        content: leftTabConfig?.content ?? (() => null),
        isActive: isLeftTabActive(),
      },
      rightTab: {
        title: rightTabConfig?.title?.(isRightTabActive()) ?? '',
        content: rightTabConfig?.content ?? (() => null),
        isActive: isRightTabActive(),
      },
      hasValidTabs: true,
    };
  }, [tabData, isLeftTabActive, isRightTabActive]);

  const getTabHeaderClasses = useCallback(
    (isActive) => {
      const baseClass = `header-${variant}`;
      const activeClass = isActive ? `active-tab-${variant}` : '';

      return [baseClass, activeClass].filter(Boolean).join(' ');
    },
    [variant]
  );

  const getTabContentClasses = useCallback((isActive) => {
    const baseClasses = ['tabs-content'];

    if (!isActive) {
      baseClasses.push('hidden-tab');
    }

    return baseClasses.join(' ');
  }, []);

  const updateDigitalData = useCallback(() => {
    if (digitalData?.pdp?.setPlansButton) {
      digitalData.pdp.setPlansButton(true);
    }
  }, [digitalData]);

  updateDigitalData();

  const computedValues = useMemo(
    () => ({
      shouldRender: processedTabData.hasValidTabs,
      containerClasses: `connect-headers-${variant} fa--connect-plan-tabs`,
      leftTabId: 'testId-ConnectTab-leftTab',
      rightTabId: 'testId-ConnectTab-rightTab',
    }),
    [processedTabData.hasValidTabs, variant]
  );

  return {
    // Processed data
    processedTabData,
    activeTab,

    // Event handlers
    handleSelectedTab,

    // State checkers
    isLeftTabActive: isLeftTabActive(),
    isRightTabActive: isRightTabActive(),

    // CSS class generators
    getTabHeaderClasses,
    getTabContentClasses,

    // Computed values
    ...computedValues,
  };
};
